import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuthContext } from "../../../core";
import { EventEntryInput } from "../../../reducers/states/event-management";
import EventEntryForm, { EventEntryFormMode } from "../event-input-form";
import {
  updateEventPayload,
  deleteEventRequest,
  updateEventRequest,
  deleteEventFailureAcknowledged,
} from "../../../actions";
import { UTCNowUtil } from "../../../utils/date-utils";
import { IRootReducer } from "../../../reducers";
import { RoleAction } from "../../../enums/permission-actions";
import useUserPermissions from "../../../hooks/user-permissions";

import CreateIncident from "../../incident-model/create-incident-model";
import { DispatchEventFeedModel } from "../../../reducers";


interface EventDetailsModalProps {
  props: {
    isOpen: boolean;
    event: EventEntryInput;
    onClose: () => void;
  };
}

const EventDetailModal: FC<EventDetailsModalProps> = ({ props }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteConfOpen, setIsDeleteConfOpen] = useState(false);
  const [isDeleteFailedOpen, setIsDeleteFailedOpen] = useState(false);

  const dispatch = useDispatch();

  const [event, setEvent] = useState<EventEntryInput>(props.event);
  const eventInputReceived = useRef<EventEntryInput>();
  const { authState } = useAuthContext();

  const [isDeleteEventDisabled] = useUserPermissions(RoleAction.DELETE_MAJOR_EVENTS);
  const [isEditEventDisabled] = useUserPermissions(RoleAction.EDIT_MAJOR_EVENTS);

  const eventDeletionStatus = useSelector((state: IRootReducer) => state.eventManagementReducer.eventDeletionStatus);

  useEffect(() => {
    // Saving this in a separate state field so it can be "reset" when an edit is canceled.
    setEvent(props.event);
  }, [props.event]);

  const handleDelete = () => {
    if (props.event?.id) {
      dispatch(deleteEventRequest(props.event.id));
    }
  };

  useEffect(() => {
    if (isDeleteConfOpen && eventDeletionStatus === "Success") {
      setIsDeleteConfOpen(false);
      props.onClose();
    } else if (eventDeletionStatus === "Failed") {
      setIsDeleteFailedOpen(true);
    } else if (!eventDeletionStatus) {
      setIsDeleteConfOpen(false);
      setIsDeleteFailedOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDeletionStatus]);

  const handleEdit = () => {
    setIsEdit(true);
  };

  const handleCancel = () => {
    // The input form doesn't change the original event during edits. So by creating a new object
    // with the original values here, the event object "changes" and the entry form has a useEffect hook
    // that is triggered and that resets the input.
    setEvent({
      ...props.event,
    });
    setIsEdit(false);
  };

  const handleEventInputChanged = (eventInfo: EventEntryInput): void => {
    // console.log("callback called - detail modal handleEventInputChanged", eventInfo);  // todo: jcavner: using for debug. delete eventually.
    eventInputReceived.current = eventInfo;
  };

  const handleSave = () => {
    setIsEdit(false);
    const newEvent = {
      ...eventInputReceived.current,
      lastModifiedBy: authState.email ?? "",
      lastModifiedDate: UTCNowUtil().toISOString(),
    } as EventEntryInput;

    if (props.event?.id) {
      dispatch(updateEventRequest(props.event.id, newEvent as updateEventPayload));
    }

    // todo: if error, feedback. if success, close dialog

    props.onClose();
  };

  const DeleteConfirmationModal = () => {
    return (
      <Dialog open={isDeleteConfOpen} fullWidth maxWidth="md" aria-labelledby="form-delete-dialog-title">
        <DialogTitle>Permanently Delete Event?</DialogTitle>
        <DialogContent>
          Are you sure? Deleting this event cannot be undone and there will be no history of its details.
        </DialogContent>
        <DialogActions>
          <>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setIsDeleteConfOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button color="inherit" variant="contained" onClick={handleDelete}>
              Delete
            </Button>
          </>
        </DialogActions>
      </Dialog>
    );
  };

  const DeleteFailedModal = () => {
    return (
      <Dialog open={isDeleteFailedOpen} fullWidth maxWidth="md" aria-labelledby="form-delete-failed-dialog-title">
        <DialogTitle>Delete Failed</DialogTitle>
        <DialogContent>
          An error occurred. You can try again now or later, or contact the administrator if you need assistance with
          this issue.
        </DialogContent>
        <DialogActions>
          <>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                dispatch(deleteEventFailureAcknowledged());
              }}
            >
              Ok
            </Button>
          </>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="form-dialog-title"
    >
      <Box sx={{
        position: "absolute",
        right: 64,
        top: 16
      }}>
        <CreateIncident
          props={{
            mostRecentSelectedEvent: {} as DispatchEventFeedModel,
          }}
        />
      </Box>
      <IconButton
        onClick={props.onClose}
        size="large"
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle id="form-dialog-title">Event Details</DialogTitle>

      <DialogContent>
        <EventEntryForm
          props={{
            event: event,
            mode: isEdit ? EventEntryFormMode.EDIT : EventEntryFormMode.VIEW,
            onEventInputChanged: handleEventInputChanged,
          }}
        />
        {DeleteConfirmationModal()}
        {DeleteFailedModal()}
      </DialogContent>

      <DialogActions>
        {isEdit ? (
          <>
            <Button color="inherit" variant="contained" onClick={handleCancel}>
              Cancel
            </Button>
            <Button color="primary" variant="contained" onClick={handleSave}>
              Save
            </Button>
          </>
        ) : (
          <>
              {!isDeleteEventDisabled &&
                <Button
                  color="inherit"
                  variant="contained"
                  onClick={() => {
                    setIsDeleteConfOpen(true);
                  }}
                >
                  Delete
                </Button>
              }
              {!isEditEventDisabled &&
                <Button color="inherit" variant="contained" onClick={handleEdit}>
                  Edit
                </Button>}
            <Button color="primary" variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EventDetailModal;
